import React from "react";
import styled from "styled-components";
import { brand_orange_deep } from "../../constants/styles";
import PagedCarousel from "../Carousel";
import SectionShell, { SectionTitle, SectionSubtitle, SectionGroup } from "../SectionShell";
import usePagedSection from "../usePagedSection";
import CardActionsOverlay from "../CardActionsOverlay";
import { Skeleton } from "../Skeleton";
import { apiGet } from "../../api";

function buildRecordingLoader(endpointPath) {
  return async (pageIdx, perPage) => {
    const { parsedResponse } = await apiGet(
      `/api/v1/homepage/recordings/${endpointPath}?page=${pageIdx + 1}&per_page=${perPage}`,
    );
    return {
      items: parsedResponse?.data ?? [],
      totalCount: parsedResponse?.meta?.total ?? 0,
    };
  };
}

const Card = styled.article`
  display: flex;
  flex-direction: column;
  gap: 10px;
  transition: transform 200ms ease;

  &:hover { transform: translateY(-4px); }
`;

const Thumbnail = styled.div`
  aspect-ratio: 16/10;
  border-radius: 12px;
  background:
    radial-gradient(ellipse at 30% 30%, rgba(255,255,255,0.25), transparent 50%),
    linear-gradient(135deg, #fbbf24, #f59e0b);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.06);
  position: relative;
  overflow: hidden;

  &::after {
    content: "";
    position: absolute;
    inset: 0;
    background: linear-gradient(180deg, transparent 55%, rgba(0,0,0,0.18));
    pointer-events: none;
  }
`;

const ThumbnailImage = styled.img`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
`;

const CardTitle = styled.div`
  color: ${brand_orange_deep};
  font-weight: 700;
  font-size: 16px;
`;

const CardSubtitle = styled.div`
  color: #6b7280;
  font-size: 12px;
  line-height: 1.5;

  [data-theme="dark"] & {
    color: #d1d5db;
  }
`;

function renderRecSkeleton(i) {
  return (
    <Card key={`sk-${i}`} aria-hidden="true">
      <Skeleton $radius="12px" style={{ aspectRatio: "16 / 10" }} />
      <Skeleton $width="55%" $height="16px" $radius="4px" />
      <Skeleton $width="85%" $height="12px" $radius="4px" />
    </Card>
  );
}

function renderRecCard(r) {
  return (
    <Card key={r.id}>
      <Thumbnail data-card-host tabIndex={0}>
        {r.image_url && (
          <ThumbnailImage src={r.image_url} alt={r.title || ""} loading="lazy" />
        )}
        <CardActionsOverlay
          resourceType={r.resource_type || "Course"}
          resourceId={r.resource_id || r.id}
          detailLink={r.detail_link}
          initialLiked={!!r.is_liked}
          initialDisliked={!!r.is_disliked}
          initialBookmarked={!!r.is_bookmarked}
        />
      </Thumbnail>
      <CardTitle>{r.title}</CardTitle>
      <CardSubtitle>{r.subtitle}</CardSubtitle>
    </Card>
  );
}

function RecGroup({ title, endpointPath, ariaLabel, emptyMessage }) {
  const loader = React.useMemo(() => buildRecordingLoader(endpointPath), [endpointPath]);
  const { items, pageIdx, setPageIdx, totalPages, loading, perPage } =
    usePagedSection({ loader });

  return (
    <SectionGroup title={title}>
      <PagedCarousel
        items={items}
        perPage={perPage}
        pageIdx={pageIdx}
        totalPages={totalPages}
        onPageChange={setPageIdx}
        loading={loading}
        renderItem={renderRecCard}
        renderSkeleton={renderRecSkeleton}
        ariaLabel={ariaLabel}
        emptyMessage={emptyMessage}
      />
    </SectionGroup>
  );
}

function RekamanCarousel() {
  return (
    <SectionShell id="rekaman">
      <SectionTitle>Rekaman</SectionTitle>
      <SectionSubtitle>
        Simak Seluruh Video dan Ikuti Ujian di Setiap Topiknya Untuk Mendapatkan Sertifikat yang Kamu Butuhkan!
      </SectionSubtitle>
      <RecGroup
        title="Live Online Classes"
        endpointPath="live_classes"
        ariaLabel="Live Online Classes"
        emptyMessage="Belum ada rekaman Live Online Classes yang tersedia."
      />
      <RecGroup
        title="Webinar"
        endpointPath="webinars"
        ariaLabel="Webinar Rekaman"
        emptyMessage="Belum ada rekaman webinar yang tersedia."
      />
    </SectionShell>
  );
}

export default React.memo(RekamanCarousel);
